import React, { useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { Close } from "react-ikonate";
import styles from "./AddCoIboModal.module.css";
import { Modal } from "../Modal/Modal";
import { InputItem } from "../InputItem/InputItem";
import { Button } from "../Button/Button";
import { formattedPhone } from "components/shared/formattedPhone";
import { useTranslation } from "react-i18next";

import classNames from "classnames";

/**
 * Modal used to add a co IBO.
 */

export function AddCoIboModal({
  onClose,
  isOpen,
  onSave,
  activeUser,
  showModal,
}) {
  const [contact, setContact] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    phone: "",
    email: "",
  });
  const [errors, setErrors] = useState({});
  // const [isSaving, setIsSaving] = useState(false);
  const [networkError, setNetworkError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {t} = useTranslation();

  //Triggers the input onChange
  const handleInputChange = (field, value) => {
    setContact((prevState) => ({
      ...prevState,
      [field]:
        field === "phone" ? value.replace(/\D/g, "").slice(0, 10) : value,
    }));

    setErrors((prevErrors) => ({
      ...prevErrors,
      [field]: "",
    }));
  };

  const validateInputs = () => {
    const errors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!contact.firstName) {
      errors.firstName = t("Please enter a first name");
    }

    if (!contact.lastName) {
      errors.lastName = t("Please enter a last name");
    }

    if (!contact.email) {
      errors.email = t("Please enter an email address");
    } else if (!emailRegex.test(contact.email)) {
      errors.email = t("Please enter a valid email address");
    } else if (contact.email === activeUser.email) {
      errors.email = t("Please enter a new email");
      showModal(true);
    } else if (networkError === t("Email already exist")) {
      showModal(true);
    }

    if (!contact.phone) {
      errors.phone = t("Please enter a phone number");
    } else {
      const cleanedPhone = contact.phone.replace(/\D/g, "");
      if (cleanedPhone.length !== 10) {
        errors.phone = ("Please enter a valid phone number");
      }
    }

    return errors;
  };

  // Handle form submission
  const handleSave = async () => {
    //Disable save button
    setIsSubmitting(true);

    const newErrors = validateInputs();
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await onSave(contact);

      if (response && response?.ok) {
        resetForm();
        onClose();
      } else if (response && response?.message === t("Email already exists")) {
        setErrors({
          email: t("Please enter a new email"),
        });
        showModal(true);
      }
    } catch (error) {
      console.error(t("Failed to save contact"), error);
      showModal(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const resetForm = () => {
    setContact({
      firstName: "",
      middleName: "",
      lastName: "",
      phone: "",
      email: "",
    });
    setErrors({});
    setNetworkError("");
    setIsSubmitting(false);
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        classNames={styles.modal}
        children={
          <>
            <div className={classNames(styles.container)}>
              <div className={styles.header}>{t("Add Co-IBO Information")}</div>

              <div className={styles.nameInputs}>
                <InputItem
                  type="text"
                  id="fName"
                  name="firstName"
                  value={contact.firstName}
                  setValue={(value) => handleInputChange("firstName", value)}
                  placeholder={t("First Name")}
                  errors={errors.firstName}
                  isPhoneNumber={false}
                />

                <InputItem
                  type="text"
                  id="mName"
                  name="middleName"
                  value={contact.middleName}
                  setValue={(value) => handleInputChange("middleName", value)}
                  placeholder={t("Middle Name")}
                  isPhoneNumber={false}
                />

                <InputItem
                  type="text"
                  id="lName"
                  name="lastName"
                  value={contact.lastName}
                  setValue={(value) => handleInputChange("lastName", value)}
                  placeholder={t("Last Name")}
                  errors={errors.lastName}
                  isPhoneNumber={false}
                />
              </div>

              <InputItem
                type="text"
                id="phone"
                name="phone"
                value={formattedPhone(contact.phone)}
                setValue={(value) => handleInputChange("phone", value)}
                placeholder={t("Phone Number")}
                errors={errors.phone}
                isPhoneNumber={true}
              />

              <InputItem
                type="text"
                id="email"
                name="email"
                value={contact.email}
                setValue={(value) => handleInputChange("email", value)}
                placeholder={t("Email Address")}
                errors={errors.email}
                isPhoneNumber={false}
              />

              <div className={styles.buttonContainer}>
                <Button
                  buttonText={t("Cancel")}
                  onClick={() => handleClose()}
                  className={styles.closeBtn}
                />
                <Button
                  buttonText={t("Save")}
                  onClick={() => handleSave()}
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </>
        }
      />
    </>
  );
}

AddCoIboModal.propTypes = {
  /**
   * 	Function called to close modal
   */
  onClose: PropTypes.func,
  /**
   * Boolean to determine if modal is open
   */
  isOpen: PropTypes.bool,
  /**
   * Function called when save is clicked
   */
  onSave: PropTypes.func,
};
